<template>
  <q-dialog
    v-bind="{
      maximized: !berkshireBreakpoints.atLeastTablet.value,
    }"
    :data-cy="DATA_CY.DIALOG"
    @hide="handleHide"
  >
    <q-card v-bind="mergedCardProps">
      <q-card-section
        class="row items-start mb-2 tablet:mb-5 !p-0 w-full justify-between"
      >
        <slot name="title" :title="title">
          <div class="max-w-[80%]">
            <DialogTitle>
              {{ title }}
            </DialogTitle>
          </div>
          <q-space />
        </slot>
        <q-btn
          v-if="showCloseButton"
          v-close-popup
          icon="close"
          flat
          round
          dense
          :data-cy="DATA_CY.CLOSE"
          aria-label="close"
          @click="handleHide"
        />
      </q-card-section>

      <div class="space-y-[0.625rem]">
        <slot name="body" />
      </div>

      <q-card-section v-if="$slots.footer" class="q-pt-md !p-0 mt-6">
        <slot name="footer" />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useBerkshireBreakpoints } from '~/composables/useBerkshireBreakpoints'
import { DATA_CY } from '~/constants/strings'

const berkshireBreakpoints = useBerkshireBreakpoints()

const props = withDefaults(
  defineProps<{
    title: string
    qCardProps?: Record<any, any>
    showCloseButton?: boolean
    useFlexLayout?: boolean // New prop to opt-in to flex layout
  }>(),
  {
    qCardProps: () => ({
      class: 'w-480px p-5',
    }),
    showCloseButton: true,
    useFlexLayout: false,
  }
)

// Define emits
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'close-prompt'): void
}>()

const mergedCardProps = computed(() => {
  const baseClass = props.qCardProps?.class || 'w-480px p-5'

  return {
    ...props.qCardProps,
    class: `${baseClass} ${props.useFlexLayout ? 'dialog-flex-layout' : ''}`,
  }
})

const handleHide = () => {
  emit('update:modelValue', false)
  emit('close-prompt')
}
</script>

<style lang="scss" scoped>
:deep(.dialog-flex-layout) {
  display: flex;
  flex-direction: column;
  max-height: 90vh;

  .q-card__section {
    // header
    &:first-child {
      flex: none;
    }

    // body
    &:nth-child(2) {
      flex: 1;
      overflow-y: auto;
      min-height: 0;
    }

    // footer
    &:last-child {
      flex: none;
    }
  }
}
</style>
